import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const roleOptions = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'reviewer', label: 'Reviewer' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'restricted', label: 'Restricted' }
];

const SuperadminPage = () => {
  const { isSuperadmin } = useAuth();

  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newOrgName, setNewOrgName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [assignEmail, setAssignEmail] = useState('');
  const [assignRole, setAssignRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const memberUserIds = useMemo(() => new Set((members || []).map(m => m.user_id)), [members]);
  const userOptions = useMemo(() => {
    return (allUsers || [])
      .filter(u => !memberUserIds.has(u.id))
      .map(u => ({ value: u.id, label: u.full_name || u.email, description: u.email }));
  }, [allUsers, memberUserIds]);

  useEffect(() => {
    const loadOrgs = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name, created_at')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setOrgs(data || []);
        if ((data || []).length && !selectedOrgId) {
          setSelectedOrgId(data[0].id);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };
    if (isSuperadmin) loadOrgs();
  }, [isSuperadmin]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedOrgId) { setMembers([]); return; }
      setMembersLoading(true);
      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select('org_id, user_id, role, user_profiles(full_name, email)')
          .eq('org_id', selectedOrgId)
          .order('role');
        if (error) throw error;
        setMembers(data || []);
      } catch (e) {
        console.error(e);
        setMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, [selectedOrgId]);

  // Load users (for superadmin assignment dropdown)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .order('email', { ascending: true })
          .limit(200);
        if (error) throw error;
        setAllUsers(data || []);
      } catch (e) {
        console.error('Failed to load users', e);
        setAllUsers([]);
      }
    };
    if (isSuperadmin) loadUsers();
  }, [isSuperadmin, selectedOrgId]);

  const createOrg = async () => {
    if (!newOrgName.trim()) return;
    setBusy(true);
    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert({ name: newOrgName.trim() })
        .select('id, name, created_at')
        .single();
      if (error) throw error;
      setOrgs(prev => [data, ...prev]);
      setNewOrgName('');
      if (!selectedOrgId) setSelectedOrgId(data.id);
    } catch (e) {
      alert(e.message || 'Failed to create organization');
    } finally {
      setBusy(false);
    }
  };

  const renameOrg = async (orgId, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ name: clean })
        .eq('id', orgId);
      if (error) throw error;
      setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, name: clean } : o));
    } catch (e) {
      alert(e.message || 'Failed to rename organization');
    }
  };

  const deleteOrg = async (orgId) => {
    if (!confirm('Delete this organization? This only succeeds if no data references it.')) return;
    try {
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId);
      if (error) throw error;
      setOrgs(prev => prev.filter(o => o.id !== orgId));
      if (selectedOrgId === orgId) setSelectedOrgId('');
    } catch (e) {
      alert(e.message || 'Failed to delete organization. Ensure it has no data.');
    }
  };

  const assignMember = async () => {
    const email = (assignEmail || '').trim().toLowerCase();
    if (!selectedOrgId) return;
    setBusy(true);
    try {
      let userRow = null;
      if (selectedUserId) {
        userRow = (allUsers || []).find(u => u.id === selectedUserId) || null;
      }
      if (!userRow && email) {
        const { data: lookup, error: uErr } = await supabase
          .from('user_profiles')
          .select('id, email, full_name')
          .ilike('email', email)
          .maybeSingle();
        if (uErr) throw uErr;
        userRow = lookup;
      }
      if (!userRow) throw new Error('No user found. Pick from the list or enter an existing email.');

      const { error: insErr } = await supabase
        .from('organization_members')
        .insert({ org_id: selectedOrgId, user_id: userRow.id, role: assignRole });
      if (insErr) throw insErr;
      setAssignEmail('');
      setSelectedUserId('');
      setAssignRole('viewer');
      // refresh
      const { data } = await supabase
        .from('organization_members')
        .select('org_id, user_id, role, user_profiles(full_name, email)')
        .eq('org_id', selectedOrgId);
      setMembers(data || []);
    } catch (e) {
      alert(e.message || 'Failed to assign member');
    } finally {
      setBusy(false);
    }
  };

  const updateMemberRole = async (userId, role) => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role })
        .eq('org_id', selectedOrgId)
        .eq('user_id', userId);
      if (error) throw error;
      setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
    } catch (e) {
      alert(e.message || 'Failed to update role');
    }
  };

  const removeMember = async (userId) => {
    if (!confirm('Remove this member from the organization?')) return;
    try {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('org_id', selectedOrgId)
        .eq('user_id', userId);
      if (error) throw error;
      setMembers(prev => prev.filter(m => m.user_id !== userId));
    } catch (e) {
      alert(e.message || 'Failed to remove member');
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-[60vh]">
          <div className="max-w-md text-center p-6 border border-border rounded-lg bg-card">
            <Icon name="Shield" size={24} />
            <h2 className="text-xl font-semibold text-foreground mt-2">Superadmin Only</h2>
            <p className="text-sm text-muted-foreground mt-1">You need superadmin privileges to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Superadmin Console</h1>
              <p className="text-sm text-muted-foreground mt-1">Manage organizations and memberships</p>
            </div>
          </div>

          {/* Orgs panel */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-foreground">Organizations</h2>
                <div className="flex items-center space-x-2">
                  <Input
                    type="text"
                    placeholder="New organization name"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className="w-64"
                  />
                  <Button variant="default" onClick={createOrg} disabled={busy || !newOrgName.trim()} iconName="Plus" iconPosition="left">Create</Button>
                </div>
              </div>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading organizations...</p>
              ) : error ? (
                <p className="text-sm text-error">{error}</p>
              ) : (
                <div className="divide-y divide-border">
                  {orgs.map(o => (
                    <div key={o.id} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          checked={selectedOrgId === o.id}
                          onChange={() => setSelectedOrgId(o.id)}
                        />
                        <input
                          className="bg-transparent border border-border rounded px-2 py-1 text-sm text-foreground"
                          defaultValue={o.name}
                          onBlur={(e) => {
                            if (e.target.value !== o.name) renameOrg(o.id, e.target.value);
                          }}
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="destructive" size="sm" onClick={() => deleteOrg(o.id)} iconName="Trash2" iconPosition="left">Delete</Button>
                      </div>
                    </div>
                  ))}
                  {orgs.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4">No organizations yet.</p>
                  )}
                </div>
              )}
            </div>

            {/* Members panel */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h2 className="text-lg font-semibold text-foreground mb-3">Members</h2>
              <div className="space-y-2 mb-3">
                <Select
                  label="Select user"
                  placeholder="Search or pick a user…"
                  options={userOptions}
                  value={selectedUserId}
                  onChange={setSelectedUserId}
                  searchable
                  clearable
                />
                <Input
                  type="email"
                  placeholder="…or type email of an existing user"
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                />
                <Select
                  options={roleOptions}
                  value={assignRole}
                  onChange={setAssignRole}
                />
                <Button variant="default" onClick={assignMember} disabled={!selectedOrgId || busy} iconName="UserPlus" iconPosition="left">
                  Assign to Org
                </Button>
                </div>
              {selectedOrgId ? (
                membersLoading ? (
                  <p className="text-sm text-muted-foreground">Loading members...</p>
                ) : (
                  <div className="space-y-2">
                    {members.map(m => (
                      <div key={m.user_id} className="flex items-center justify-between border border-border rounded px-2 py-2">
                        <div>
                          <div className="text-sm font-medium text-foreground">{m.user_profiles?.full_name || m.user_profiles?.email || m.user_id}</div>
                          <div className="text-xs text-muted-foreground">{m.user_profiles?.email}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Select
                            options={roleOptions}
                            value={m.role}
                            onChange={(val) => updateMemberRole(m.user_id, val)}
                            className="w-32"
                          />
                          <Button variant="destructive" size="sm" onClick={() => removeMember(m.user_id)} iconName="UserMinus" />
                        </div>
                      </div>
                    ))}
                    {members.length === 0 && (
                      <p className="text-sm text-muted-foreground">No members yet.</p>
                    )}
                  </div>
                )
              ) : (
                <p className="text-sm text-muted-foreground">Select an organization to view members.</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SuperadminPage;
